import {
  ArrowLeft,
  Bot,
  Check,
  CheckCircle2,
  FileText,
  LoaderCircle,
  Pencil,
  RefreshCw,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { PageHeader, useRouter } from "@tribunalea/ui";
import { useApi } from "../App.jsx";

const pendingStatuses = new Set([
  "uploading",
  "queued",
  "extracting",
  "indexing",
]);
const statusLabels = {
  uploading: "Guardando PDF",
  queued: "En cola",
  extracting: "Extrayendo texto",
  indexing: "Creando índice",
  ready: "Lista",
  failed: "Con error",
};

function megabytes(bytes) {
  const value = Number(bytes || 0) / 1024 / 1024;
  return `${new Intl.NumberFormat("es-AR", {
    maximumFractionDigits: 1,
  }).format(value)} MB`;
}

export function NotebookPage({ notebookId, management = false }) {
  const api = useApi();
  const { navigate } = useRouter();
  const inputRef = useRef(null);
  const [notebook, setNotebook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [limits, setLimits] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");
  const [editingSourceId, setEditingSourceId] = useState(null);
  const [sourceName, setSourceName] = useState("");
  const [savingSourceId, setSavingSourceId] = useState(null);
  const focusedSourceId = new URLSearchParams(window.location.search)
    .get("source");

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      setNotebook(await api.getNotebook(notebookId));
      setError("");
    } catch (reason) {
      setError(reason.message);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [api, notebookId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let active = true;
    api.limits()
      .then((nextLimits) => {
        if (active) setLimits(nextLimits);
      })
      .catch(() => {
        if (active) setLimits(null);
      });
    return () => {
      active = false;
    };
  }, [api]);

  useEffect(() => {
    if (!notebook?.sources.some(({ status }) => pendingStatuses.has(status))) {
      return undefined;
    }
    const timer = window.setInterval(() => void load({ quiet: true }), 2_500);
    return () => window.clearInterval(timer);
  }, [load, notebook]);

  useEffect(() => {
    if (!focusedSourceId || !notebook) return;
    window.setTimeout(() => {
      document.getElementById(`library-source-${focusedSourceId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  }, [focusedSourceId, notebook]);

  async function upload(file) {
    if (!file || uploading) return;
    if (limits?.libraryMaxBytes && file.size > limits.libraryMaxBytes) {
      setError(
        `El PDF pesa ${megabytes(file.size)} y el límite es ${megabytes(
          limits.libraryMaxBytes,
        )}.`,
      );
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setUploading(true);
    setError("");
    try {
      await api.uploadNotebookSource(notebookId, file);
      await load({ quiet: true });
    } catch (reason) {
      setError(
        reason.code === "library_file_too_large" && limits?.libraryMaxBytes
          ? `El PDF supera el límite de ${megabytes(limits.libraryMaxBytes)}.`
          : reason.message,
      );
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function removeSource(source) {
    if (!window.confirm(`¿Quitar “${source.name || source.fileName}” del cuaderno?`)) return;
    try {
      await api.deleteNotebookSource(notebookId, source.id);
      await load({ quiet: true });
    } catch (reason) {
      setError(reason.message);
    }
  }

  function editSource(source) {
    setEditingSourceId(source.id);
    setSourceName(source.name || source.fileName);
    setError("");
  }

  function cancelSourceEdit() {
    setEditingSourceId(null);
    setSourceName("");
  }

  async function saveSourceName(event, source) {
    event.preventDefault();
    const name = sourceName.trim();
    if (!name || savingSourceId) return;
    setSavingSourceId(source.id);
    setError("");
    try {
      await api.updateNotebookSource(notebookId, source.id, { name });
      cancelSourceEdit();
      await load({ quiet: true });
    } catch (reason) {
      setError(reason.message);
    } finally {
      setSavingSourceId(null);
    }
  }

  if (loading && !notebook) {
    return (
      <div className="page">
        <div className="loading-card">
          <LoaderCircle className="is-spinning" size={18} />
          Abriendo cuaderno…
        </div>
      </div>
    );
  }

  if (!notebook) {
    return (
      <div className="page">
        <p className="form-error">{error || "No encontramos el cuaderno."}</p>
      </div>
    );
  }

  return (
    <div className="page notebook-page">
      <button
        className="back-link"
        type="button"
        onClick={() => navigate(
          management ? "/biblioteca/administrar" : "/biblioteca",
        )}
      >
        <ArrowLeft size={16} />
        {management ? "Administrar biblioteca" : "Biblioteca IA"}
      </button>
      <PageHeader
        eyebrow="Cuaderno"
        title={notebook.name}
        description={notebook.description || "Fuentes persistentes para consultar con Justiniano."}
        actions={!management ? (
          <button
            className="primary-button"
            type="button"
            disabled={!notebook.readySourceCount}
            onClick={() =>
              navigate(`/justiniano?notebook=${encodeURIComponent(notebook.id)}`)}
          >
            <Bot size={17} />
            Consultar en Justiniano
          </button>
        ) : null}
      />

      <section
        className={`library-dropzone ${dragging ? "is-dragging" : ""}`}
        onDragEnter={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setDragging(false);
          }
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          void upload(event.dataTransfer.files?.[0]);
        }}
      >
        {uploading
          ? <LoaderCircle className="is-spinning" size={28} />
          : <UploadCloud size={28} />}
        <div>
          <strong>
            {uploading ? "Subiendo PDF…" : "Arrastrá un PDF al cuaderno"}
          </strong>
          <span>
            {limits?.libraryMaxBytes && limits?.libraryMaxPages
              ? `Máximo ${megabytes(limits.libraryMaxBytes)} y ${limits.libraryMaxPages} páginas por PDF. `
              : ""}
            También podés seleccionarlo desde tu equipo.
          </span>
        </div>
        <button
          className="secondary-button"
          type="button"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          Seleccionar PDF
        </button>
        <input
          ref={inputRef}
          hidden
          type="file"
          accept="application/pdf,.pdf"
          onChange={(event) => void upload(event.target.files?.[0])}
        />
      </section>

      {error && <p className="form-error">{error}</p>}

      <section className="section-block">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Fuentes</span>
            <h2>Documentos del cuaderno</h2>
          </div>
          <span>{notebook.sourceCount} PDF</span>
        </div>
        {notebook.sources.length ? (
          <div className="library-source-list">
            {notebook.sources.map((source) => (
              <article
                className={`library-source is-${source.status} ${
                  focusedSourceId === source.id ? "is-focused" : ""
                }`}
                id={`library-source-${source.id}`}
                key={source.id}
              >
                <span className="library-source-icon">
                  {source.status === "ready"
                    ? <CheckCircle2 size={20} />
                    : pendingStatuses.has(source.status)
                      ? <LoaderCircle className="is-spinning" size={20} />
                      : <FileText size={20} />}
                </span>
                <div className="library-source-copy">
                  {editingSourceId === source.id ? (
                    <form
                      className="library-source-name-form"
                      onSubmit={(event) => void saveSourceName(event, source)}
                    >
                      <input
                        aria-label="Nombre del PDF"
                        autoFocus
                        maxLength={160}
                        value={sourceName}
                        onChange={(event) => setSourceName(event.target.value)}
                      />
                      <button
                        className="icon-button"
                        type="submit"
                        title="Guardar nombre"
                        disabled={!sourceName.trim() || savingSourceId === source.id}
                      >
                        {savingSourceId === source.id
                          ? <LoaderCircle className="is-spinning" size={15} />
                          : <Check size={15} />}
                      </button>
                      <button
                        className="icon-button"
                        type="button"
                        title="Cancelar"
                        disabled={savingSourceId === source.id}
                        onClick={cancelSourceEdit}
                      >
                        <X size={15} />
                      </button>
                    </form>
                  ) : (
                    <strong title={source.fileName}>
                      {source.name || source.fileName}
                    </strong>
                  )}
                  <span>
                    {statusLabels[source.status]}
                    {source.pageCount ? ` · ${source.pageCount} páginas` : ""}
                    {source.vectorized ? " · búsqueda semántica" : ""}
                  </span>
                  {source.error && (
                    <small className="is-error">{source.error.message}</small>
                  )}
                  {source.warnings?.map((warning) => (
                    <small className="is-warning" key={warning}>{warning}</small>
                  ))}
                </div>
                <div className="library-source-actions">
                  {editingSourceId !== source.id && (
                    <button
                      className="icon-button"
                      type="button"
                      title="Editar nombre"
                      onClick={() => editSource(source)}
                    >
                      <Pencil size={15} />
                    </button>
                  )}
                  {source.status === "ready" && (
                    <button
                      className="secondary-button"
                      type="button"
                      onClick={() => api.openNotebookSource(notebookId, source.id)}
                    >
                      Ver PDF
                    </button>
                  )}
                  {source.status === "failed" && (
                    <button
                      className="secondary-button"
                      type="button"
                      onClick={async () => {
                        await api.retryNotebookSource(notebookId, source.id);
                        await load({ quiet: true });
                      }}
                    >
                      <RefreshCw size={14} />
                      Reintentar
                    </button>
                  )}
                  <button
                    className="icon-button danger"
                    type="button"
                    title="Quitar fuente"
                    onClick={() => void removeSource(source)}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="empty-panel">
            <FileText size={25} />
            <h3>Este cuaderno todavía no tiene fuentes</h3>
            <p>Agregá un PDF para comenzar a procesarlo.</p>
          </div>
        )}
      </section>
    </div>
  );
}
